/**
 * My residents screen (active tab 3 of 3) — T1717 M3, P0-1/P0-2/P0-3/P0-4.
 *
 * The home's own roster: list the active residents, add one, correct one, and
 * take one off the list. Reads `GET /api/residents` (never gated) and
 * `GET /api/meta` (for the two server-decided capability flags); writes go to
 * `POST /api/residents`, `POST /api/residents/:id` and
 * `POST /api/residents/:id/active`, which are the four shapes
 * `server/request-routes.ts` publishes and the only ones this screen knows.
 *
 * ⛔ THE CAPABILITY IS THE SERVER'S TO DECIDE, NEVER THIS FILE'S. `residentsWritable`
 * comes from the same predicate the write routes enforce, and an ABSENT key
 * means CLOSED — the same defaulting rule `RequestForm` already follows, and
 * for the same reason: defaulting open would promise a form the server refuses.
 * Closing the form here is a courtesy, not the control; the server refuses
 * independently and this screen renders whatever it answers.
 *
 * ⛔ ONE EDITABLE FORM AT A TIME, and that is a real rule rather than tidiness.
 * The add form and an open edit row would otherwise render two fields carrying
 * the SAME label text at once, which makes `getByLabel('Next of kin phone')` —
 * a live e2e portal needle — ambiguous and blanks a serial lane (DD11). It is
 * also the simpler screen: this client bounced off a prior tool for having too
 * many buttons.
 *
 * ⛔ NO PRICE, NO FIGURE, NO EURO SIGN (Rule 1). Pinned in
 * test/client/my-residents.spec.tsx with a planted positive control.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { isResidentId, type ApiClient } from '../api.js';
import { myResidentsCopy, sharedCopy } from '../copy.js';

/** ⛔ THE SERVER'S OWN SHAPE (`core/residents-repo.ts`'s `ResidentListItem`),
 *  mirrored key for key. Every roster route answers with it, so the list route
 *  and the three write routes cannot render differently. */
type Resident = {
  id: number;
  displayName: string;
  room: string;
};

/** What the nurse has typed. ⚠️ BOTH FIELDS ARE REQUIRED since T1868 M6, so the
 *  "a blank becomes null on the wire" rule the next-of-kin pair needed has no
 *  field left to apply to. */
type Draft = {
  displayName: string;
  room: string;
};

const EMPTY_DRAFT: Draft = { displayName: '', room: '' };

/** Every body a roster route can answer with, in one type: `{resident}` on a
 *  write, `{errors}` on a 400, `{error}` (plus `residentId` on the removed
 *  arm) on a refusal. */
type WriteBody = {
  resident?: Resident;
  errors?: Record<string, string>;
  error?: string;
  residentId?: number;
};

function draftFrom(r: Resident): Draft {
  return {
    displayName: r.displayName,
    room: r.room,
  };
}

/** ⚠️ THE `orNull` HELPER WENT WITH THE NEXT-OF-KIN PAIR (T1868 M6). It existed
 *  because the edit route sends the WHOLE row back, so an emptied OPTIONAL field
 *  had to say "clear this" as `null` rather than as `''`. Neither surviving field
 *  is optional: an emptied one is a 400, not a clear. */
function payloadFrom(d: Draft): Record<string, string | null> {
  return {
    displayName: d.displayName.trim(),
    room: d.room.trim(),
  };
}

/**
 * The URL for a roster write, or `null` when the id is not one this client will
 * put in a path.
 *
 * ⛔ THE ID IS CHECKED, NOT TRUSTED, EVEN THOUGH THE TYPE SAYS `number`. It
 * comes off the wire, and a type is a claim about the source, not a guarantee
 * about the bytes: a malformed roster row would otherwise be interpolated
 * straight into `/api/residents/${id}` and send `undefined` or a path segment of
 * the server's choosing.
 * ⚠️ AND THE `resident_removed` ARM GOES THROUGH IT TOO, from T1717 M7 (Captain
 * deltas #26). The M3 docblock asserted that already and it was not so: the 409
 * site checks `typeof residentId === 'number'` — which admits `NaN`, `1.5` and
 * `-1` — and `reactivate` then built its URL by template literal. It now calls
 * `residentPath(offer.residentId, '/active')` and refuses a null.
 */
function residentPath(id: number, suffix = ''): string | null {
  if (!isResidentId(id)) return null;
  return `/api/residents/${String(id)}${suffix}`;
}

/**
 * One refusal → one sentence, for all three write routes.
 *
 * ⛔ THE 409s GET THEIR OWN COPY rather than a generic failure: "already on your
 * list" and "your list is full" are both things the nurse can act on, and a
 * bare "something did not work" on either is what makes a screen feel broken.
 * `resident_removed` is NOT here — it is an offer, not a message (DD10), and is
 * handled at its call site.
 */
function refusalMessage(status: number, body: WriteBody | undefined, name: string): string {
  const code = body?.error;
  if (status === 403 || code === 'residents_readonly') return myResidentsCopy.writesClosed;
  if (code === 'resident_exists') return myResidentsCopy.alreadyOnList(name);
  if (code === 'resident_limit') return myResidentsCopy.listFull;
  if (status === 404 || code === 'not_found') return myResidentsCopy.goneFromList;
  return sharedCopy.genericError;
}

export type MyResidentsProps = { api: ApiClient };

export function MyResidents({ api }: MyResidentsProps) {
  const [items, setItems] = useState<Resident[] | null>(null);
  const [writable, setWritable] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [addNotice, setAddNotice] = useState<string | null>(null);
  /**
   * ⭐⭐ THE ADD FORM IS OPENED BY HER, NEVER BY A STATE CHANGE — M3 review,
   * webapp-testing anomaly, REPRODUCED LIVE on the private stack.
   *
   * ⛔ WHAT THE TESTER SAW, AND WHY IT WAS NOT A KEYING BUG. The add form
   * carries the SAME four labels as a row's inline edit form and sits directly
   * under the last row. It is hidden while a row is open, so tapping `Save` or
   * `Cancel` made a four-field form MATERIALISE at the foot of the list with no
   * click — which reads exactly like "the last row opened its own editor". The
   * roster state was correct throughout (`key={r.id}`, ids in component state,
   * proven by the reorder arm in the spec); what was wrong is that a form
   * appeared unbidden and was indistinguishable from a row's.
   *
   * ⇒ It is now behind its own control, so the only thing that can appear at
   * the foot of the list without a tap is a BUTTON. `startEdit` closes it, so
   * leaving an edit never re-opens it either.
   */
  const [adding, setAdding] = useState(false);
  /** The `409 resident_removed` offer (DD10): the id the server handed back,
   *  and the name to say it with. */
  const [putBack, setPutBack] = useState<{ residentId: number; name: string } | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  /** A refusal that belongs to ONE row, rendered in that row. */
  const [rowNotice, setRowNotice] = useState<{ id: number; message: string } | null>(null);
  /** Two-tap remove: the first tap arms this row, the second one sends. */
  const [armedId, setArmedId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * ⭐ THE OPEN EDIT FORM TAKES FOCUS (M3 review, ux). Tapping "Edit" replaces
   * the row's text with four fields several lines further down; without this,
   * focus stays on a button that no longer exists and a screen-reader nurse is
   * told nothing happened. This is the request-sent panel's mechanism
   * (`RequestForm.tsx`'s `successTitleRef`) applied to a control rather than a
   * heading — so no `tabIndex` is needed, an input is focusable already, and no
   * ring has to be suppressed because this one IS a control.
   */
  const editNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editingId !== null) editNameRef.current?.focus();
  }, [editingId]);

  /**
   * ⭐ AND SO DOES THE ADD FORM — T1717 M7, Captain deltas #20. The same act
   * from the nurse's side ("a form just appeared, several lines down") had the
   * treatment on one path and not the other: "Add a resident" replaced the
   * button with four fields and left focus on a button that no longer exists.
   * ⚠️ A SECOND REF, not `editNameRef` reused: the two forms are never on screen
   * together (the add block is gated on `editingId === null`), but one ref
   * bound into two JSX trees is a hazard the moment that gate changes, and
   * `ref` is last-writer-wins with no error.
   */
  const addNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (adding) addNameRef.current?.focus();
  }, [adding]);

  /**
   * ⭐⭐ THE OUTCOME OF A WRITE IS ANNOUNCED, AND FOCUS GOES SOMEWHERE USEFUL —
   * T1717 M7, ux-review WARN on the live drive.
   *
   * ⛔ WHAT THE GAP WAS. Refusals had a voice on this screen from M3; SUCCESSES
   * did not. After Save (add or edit) and after a confirmed Remove the screen
   * just re-rendered — no sentence said the write had happened, and focus
   * dropped to `<body>`, so a keyboard nurse restarted her tab walk from the top
   * of the document and a screen-reader nurse was told nothing at all.
   *
   * ⛔ TWO SEPARATE MECHANISMS, and they fail differently, so they are separate:
   *   · `outcome` + the `role="status"` region is the ANNOUNCEMENT (the AT half);
   *   · `focusAfter` + the effect below is the PLACE (the keyboard half).
   * A live region alone leaves focus on `<body>`; a focus move alone announces
   * only the element's own name, never what happened.
   *
   * ⚠️ THE LIVE REGION IS RENDERED UNCONDITIONALLY, EMPTY, from first paint.
   * A `role="status"` element that is INSERTED carrying its text is, in several
   * screen readers, not announced at all — the region has to exist before the
   * text arrives for the mutation to be observed. That is why the render below
   * has no `{outcome && ...}` guard around the element itself.
   *
   * ⚠️ THE FOCUS MOVE RUNS FROM AN EFFECT, not from the write handler. The
   * targets (the add form's name field; the row's Edit button, for an edit AND
   * for a put-back; the heading) only
   * exist after `load()` has re-rendered the list, so focusing inside the handler
   * would reach a node that is about to be replaced. `focusAfter` is cleared by
   * the effect so a later unrelated re-render cannot steal focus a second time.
   */
  const [outcome, setOutcome] = useState<string | null>(null);
  const [focusAfter, setFocusAfter] = useState<{ kind: 'add' | 'edit' | 'remove'; id?: number } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** The row Edit button's DOM id, so the effect can find it after `load()`
   *  has re-rendered the list. One helper, used by the render and the effect,
   *  because two spellings of an id is how this silently focuses nothing. */
  const editButtonId = (id: number): string => `res-row-edit-${String(id)}`;

  useEffect(() => {
    if (focusAfter === null) return;
    if (focusAfter.kind === 'add') addNameRef.current?.focus();
    else if (focusAfter.kind === 'edit' && focusAfter.id !== undefined) {
      document.getElementById(editButtonId(focusAfter.id))?.focus();
    } else {
      // ⛔ THE HEADING, and it carries `tabIndex={-1}` for exactly this reason —
      // the same mechanism `RequestForm`'s success panel uses. The row she was
      // standing on has just been removed, so there is no control to return to;
      // the heading re-states where she is.
      headingRef.current?.focus();
    }
    setFocusAfter(null);
  }, [focusAfter]);

  /**
   * ⛔ BOTH READS, EVERY TIME, INCLUDING AFTER A WRITE. The roster is re-read
   * from the server rather than spliced locally (the rule `RequestForm` already
   * follows), and the flags come back with it so a capability that closed
   * server-side cannot leave a form on screen that no longer works.
   */
  const load = async (): Promise<void> => {
    const [list, meta] = await Promise.all([
      api.get<{ residents: Resident[] }>('/api/residents'),
      api.get<{ residentsWritable?: boolean }>('/api/meta'),
    ]);
    if (list.kind === 'network-error' || meta.kind === 'network-error') {
      setLoadError(sharedCopy.networkError);
      return;
    }
    if (list.status !== 200 || meta.status !== 200) {
      setLoadError(myResidentsCopy.loadError);
      return;
    }
    setLoadError(null);
    setItems(list.body.residents);
    /**
     * ⛔ A TRACKED ROW THAT HAS VANISHED TAKES ITS STATE WITH IT — T1717 M7,
     * Captain deltas #21. `editingId` was cleared only when the CAPABILITY
     * closed, so if the row being edited was gone from the fresh roster (a
     * second nurse at the same home removed the same resident, which is the
     * ordinary case on a shared ward device) the screen dead-ended: no row
     * matched `editingId === r.id` so no edit form rendered, AND the add block
     * is gated on `editingId === null` so the "Add a resident" button was hidden
     * too. Nothing on screen said why, and nothing but a reload cleared it.
     * ⚠️ `armedId` for the same reason: a two-tap remove armed against a row
     * that no longer exists would otherwise stay armed against nothing.
     * ⚠️ Functional updates, because `load()` runs from an effect and from every
     * write — the closure's `editingId` may be a render behind.
     */
    const presentIds = new Set(list.body.residents.map((r) => r.id));
    setEditingId((prev) => (prev !== null && !presentIds.has(prev) ? null : prev));
    setArmedId((prev) => (prev !== null && !presentIds.has(prev) ? null : prev));
    // ⚠️ ABSENT MEANS FALSE — the closed posture. See the file header.
    const writableNow = meta.body.residentsWritable === true;
    setWritable(writableNow);
    // ⛔ AND AN OPEN EDIT CLOSES WITH IT. The capability can go from open to
    // closed between two reads (the acknowledgement is Alex's to unset as well
    // as to set), and a form left on screen after that is an affordance the
    // server will refuse — the same reason the add form and the row controls
    // are gated (M3 review, security finding).
    if (!writableNow) setEditingId(null);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ⚠️ `setOutcome(null)` belongs here and at the top of the other two writers:
   *  a "Saved." left standing beside a NEW refusal reads as though both are
   *  true of the same act. The outcome line describes the LAST completed write
   *  and nothing else. */
  function clearAddFeedback(): void {
    setOutcome(null);
    setAddErrors({});
    setAddNotice(null);
    setPutBack(null);
  }

  async function addResident(e: Event): Promise<void> {
    e.preventDefault();
    const name = draft.displayName.trim();
    clearAddFeedback();
    setBusy(true);
    const res = await api.post<WriteBody>('/api/residents', payloadFrom(draft));
    setBusy(false);

    if (res.kind === 'network-error') {
      setAddNotice(sharedCopy.networkError);
      return;
    }
    if (res.status === 201) {
      setDraft(EMPTY_DRAFT);
      // The form stays open by design (see the `adding` docblock); the outcome
      // line is what makes that defensible, and focus returns to the field she
      // would type in next rather than to `<body>`.
      setOutcome(myResidentsCopy.addedNotice(name));
      await load();
      setFocusAfter({ kind: 'add' });
      return;
    }
    if (res.status === 400 && res.body?.errors) {
      setAddErrors(res.body.errors);
      return;
    }
    // ⭐ DD10 — the one refusal that is an OFFER. The id is the caller's own
    // home's row by construction (the server's lookup is home-scoped).
    // ⛔ `isResidentId`, NOT `typeof === 'number'` — T1717 M7, security review.
    // This site admitted `NaN`, `1.5` and `-1` into `putBack` state, where
    // `reactivate`'s own guard then had to catch them; one predicate, three
    // sites, and now they cannot disagree. Arm: my-residents.spec.tsx
    // "a malformed resident_removed id never becomes an offer".
    if (res.status === 409 && res.body?.error === 'resident_removed' && isResidentId(res.body.residentId)) {
      setPutBack({ residentId: res.body.residentId, name });
      return;
    }
    setAddNotice(refusalMessage(res.status, res.body, name));
  }

  async function reactivate(offer: { residentId: number; name: string }): Promise<void> {
    setOutcome(null);
    /**
     * ⛔ THROUGH `residentPath`, NOT A TEMPLATE LITERAL — T1717 M7, Captain
     * deltas #26. The docblock on `residentPath` claimed the `resident_removed`
     * arm already followed its guard; it did not. The 409 site checks only
     * `typeof residentId === 'number'`, which admits `NaN`, `1.5` and `-1`, and
     * this function then interpolated whatever that was straight into a URL.
     * One guard, four write paths, and now the claim is true.
     */
    const path = residentPath(offer.residentId, '/active');
    if (path === null) {
      setPutBack(null);
      setAddNotice(myResidentsCopy.goneFromList);
      return;
    }
    setBusy(true);
    // ⚠️ `path`, never a re-built template literal — the guard above is only a
    // guard if its RESULT is what gets sent.
    const res = await api.post<WriteBody>(path, { active: true });
    setBusy(false);
    if (res.kind === 'network-error') {
      setAddNotice(sharedCopy.networkError);
      return;
    }
    /**
     * ⛔ THE OFFER SURVIVES A REFUSAL — T1717 M7, Captain deltas #24.
     * `setPutBack(null)` used to run BEFORE the status branch, so a reactivate
     * that failed for any transient reason retired the only control that could
     * retry it: the nurse was left with a notice, no button, and no way back to
     * the offer except re-typing the name to earn a second 409. It is cleared on
     * SUCCESS only, where `load()` re-renders the row into the list anyway.
     * ⚠️ A 404/`not_found` is a refusal like any other here. It reads as "gone
     * from your list", which is honest, and keeping the (now inert) button costs
     * one tap that produces the same sentence — strictly better than removing
     * the affordance for every transient 500 as well.
     */
    if (res.status === 200) {
      setPutBack(null);
      setDraft(EMPTY_DRAFT);
      /**
       * ⭐ T1717 rework (Captain's live drive, WARN): THE PUT-BACK ANNOUNCES
       * AND MOVES FOCUS LIKE THE OTHER THREE WRITES. As shipped it was the one
       * write on this screen that said nothing and left focus on `<body>` when
       * its button vanished. The outcome names who came back; focus goes to
       * that row's own Edit button, because the person is back on the list and
       * the row is where she now is (the add form has just been cleared, so
       * its name field would be an empty control with nothing to say).
       */
      setOutcome(myResidentsCopy.putBackNotice(offer.name));
      await load();
      setFocusAfter({ kind: 'edit', id: offer.residentId });
      return;
    }
    setAddNotice(refusalMessage(res.status, res.body, offer.name));
  }

  function startEdit(r: Resident): void {
    setEditingId(r.id);
    setEditDraft(draftFrom(r));
    setEditErrors({});
    setRowNotice(null);
    setArmedId(null);
    setAdding(false);
    clearAddFeedback();
  }

  async function saveEdit(e: Event, r: Resident): Promise<void> {
    e.preventDefault();
    setOutcome(null);
    setEditErrors({});
    setRowNotice(null);
    const path = residentPath(r.id);
    if (path === null) {
      setRowNotice({ id: r.id, message: myResidentsCopy.goneFromList });
      return;
    }
    setBusy(true);
    const res = await api.post<WriteBody>(path, payloadFrom(editDraft));
    setBusy(false);

    if (res.kind === 'network-error') {
      setRowNotice({ id: r.id, message: sharedCopy.networkError });
      return;
    }
    if (res.status === 200) {
      // ⛔ ONLY IF THIS ROW IS STILL THE OPEN ONE. A late 200 for row A must not
      // close row B's form and throw away what is typed in it (M3 review).
      setEditingId((open) => (open === r.id ? null : open));
      setOutcome(myResidentsCopy.savedNotice(editDraft.displayName.trim()));
      await load();
      // Back to the control that opened the form she has just closed — the row
      // is where she was, and the Edit button is where she left from.
      setFocusAfter({ kind: 'edit', id: r.id });
      return;
    }
    if (res.status === 400 && res.body?.errors) {
      setEditErrors(res.body.errors);
      return;
    }
    setRowNotice({ id: r.id, message: refusalMessage(res.status, res.body, editDraft.displayName.trim()) });
  }

  async function removeResident(r: Resident): Promise<void> {
    setOutcome(null);
    const path = residentPath(r.id, '/active');
    if (path === null) {
      setRowNotice({ id: r.id, message: myResidentsCopy.goneFromList });
      return;
    }
    setBusy(true);
    const res = await api.post<WriteBody>(path, { active: false });
    setBusy(false);
    // ⛔ ONLY THIS ROW'S CONFIRM IS DISARMED, for the same reason the edit above
    // is scoped: a late answer for row A must not silently un-arm row B.
    setArmedId((armed) => (armed === r.id ? null : armed));
    if (res.kind === 'network-error') {
      setRowNotice({ id: r.id, message: sharedCopy.networkError });
      return;
    }
    if (res.status === 200) {
      setOutcome(myResidentsCopy.removedNotice(r.displayName));
      await load();
      setFocusAfter({ kind: 'remove' });
      return;
    }
    setRowNotice({ id: r.id, message: refusalMessage(res.status, res.body, r.displayName) });
  }

  /** The four fields, rendered identically for the add form and the edit row.
   *  ⛔ ONE RENDERER, because two copies is how the edit ends up offering a
   *  field the create does not — the same reason the server validates both
   *  routes through one function. `idPrefix` keeps the `for`/`id` pairs unique. */
  function fields(
    idPrefix: string,
    values: Draft,
    errors: Record<string, string>,
    onChange: (patch: Partial<Draft>) => void,
    /** The control that takes focus when this form opens; typed off the ref
     *  itself so the two cannot drift. */
    firstFieldRef?: typeof editNameRef,
  ) {
    return (
      <>
        <label class="field-label" for={`${idPrefix}-name`}>
          {myResidentsCopy.nameLabel}
        </label>
        <input
          id={`${idPrefix}-name`}
          ref={firstFieldRef}
          type="text"
          maxLength={120}
          aria-invalid={errors['displayName'] ? 'true' : undefined}
          value={values.displayName}
          onInput={(e) => onChange({ displayName: (e.target as HTMLInputElement).value })}
        />
        {errors['displayName'] && <p class="field-error">{errors['displayName']}</p>}

        <label class="field-label" for={`${idPrefix}-room`}>
          {myResidentsCopy.roomLabel}
        </label>
        <input
          id={`${idPrefix}-room`}
          type="text"
          maxLength={20}
          aria-invalid={errors['room'] ? 'true' : undefined}
          value={values.room}
          onInput={(e) => onChange({ room: (e.target as HTMLInputElement).value })}
        />
        {/* ⭐ M3 review (ux): the live roster showed rooms typed as "Room 12",
            which the row then rendered as "Room Room 12". The hint asks for the
            bare value; `myResidentsCopy.roomLine` handles what is already
            stored, because a hint cannot fix a row written last month. */}
        <p class="field-hint">{myResidentsCopy.roomHint}</p>
        {errors['room'] && <p class="field-error">{errors['room']}</p>}

      </>
    );
  }

  if (loadError) {
    return (
      <div class="screen my-residents-screen">
        <p class="field-error" role="alert">
          {loadError}
        </p>
        <button type="button" class="btn btn--primary" onClick={() => void load()}>
          {sharedCopy.tryAgain}
        </button>
      </div>
    );
  }

  if (!items) {
    return (
      <p class="loading-notice" role="status">
        {sharedCopy.loading}
      </p>
    );
  }

  return (
    <div class="screen my-residents-screen">
      {/* ⛔ `tabIndex={-1}` IS NOT DECORATION — it is what makes the heading a
          legal focus target after a removal takes away the row she was on.
          Same mechanism as `RequestForm`'s success panel. */}
      <h1 ref={headingRef} tabIndex={-1}>
        {myResidentsCopy.title}
      </h1>

      {/* ⛔ RENDERED ALWAYS, EMPTY UNTIL THERE IS SOMETHING TO SAY — see the
          `outcome` docblock. A live region inserted WITH its text is not
          reliably announced; one that already exists and then changes is. */}
      <p class="notice" role="status">
        {outcome ?? ''}
      </p>

      {/* ⛔ THE WHOLE REFUSAL, IN ONE LINE, ABOVE THE LIST. The roster still
          renders — reads are never gated (DD1) — so she can see her list and be
          told plainly why she cannot change it. */}
      {!writable && <p class="notice">{myResidentsCopy.writesClosed}</p>}

      {items.length === 0 ? (
        <p>{writable ? myResidentsCopy.emptyState : myResidentsCopy.emptyStateClosed}</p>
      ) : (
        <ul class="my-residents-list">
          {items.map((r) => (
            <li key={r.id} class="my-residents-row">
              {/* ⛔ `writable &&` HERE TOO. The inline edit form was the one
                  write affordance this screen did not gate, so a roster opened
                  before the capability closed kept an editable form on screen
                  (M3 review, security finding). `load()` also clears
                  `editingId` when the flag comes back false, so the two agree
                  whichever of them notices first. */}
              {writable && editingId === r.id ? (
                <form onSubmit={(e) => void saveEdit(e, r)}>
                  {fields(
                    'res-edit',
                    editDraft,
                    editErrors,
                    (patch) => setEditDraft((prev) => ({ ...prev, ...patch })),
                    editNameRef,
                  )}
                  <div class="my-residents-actions">
                    <button type="submit" class="btn btn--primary" disabled={busy}>
                      {myResidentsCopy.saveButton}
                    </button>
                    <button
                      type="button"
                      class="btn btn--secondary"
                      disabled={busy}
                      onClick={() => setEditingId(null)}
                    >
                      {myResidentsCopy.cancelEditButton}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <span class="my-residents-name">{r.displayName}</span>
                  <span class="my-residents-detail">{myResidentsCopy.roomLine(r.room)}</span>
                  {writable &&
                    (armedId === r.id ? (
                      /* The confirm block reuses `.cancel-confirm*` — the same
                         prompt/scope-note/two-buttons shape My requests uses for
                         its own destructive second tap, and the same treatment.
                         A second set of classes saying the same thing is how the
                         two drift apart. */
                      <div class="cancel-confirm">
                        <p class="cancel-confirm-prompt">
                          {myResidentsCopy.removeConfirmPrompt(r.displayName)}
                        </p>
                        <p class="cancel-confirm-scope">{myResidentsCopy.removeScopeNote}</p>
                        <div class="cancel-confirm-actions">
                          {/* ⭐⭐ T1749 M7 (R12 collision, decided here and
                              recorded). Q140 retires the portal's outline class and re-points
                              the plain sites to `secondary` — which would have put
                              TWO IDENTICAL secondary buttons side by side in this
                              confirm, one of which destroys a resident record.
                              DESIGN.md §5.1 already rules the pair: "the tap that
                              DESTROYS wears the destructive grammar" (its worked
                              reference is `admin/DestinationList.tsx`, and
                              `MyRequests.tsx`'s cancel confirm was fixed to this
                              shape at T1723 M7's impeccable W5 while this screen
                              was not). ⛔ So the destroying limb moves to
                              `destructive-quiet` and the SAFE limb keeps the
                              secondary the ruling gives it. Neither the words nor
                              the order changed, so every `getByRole` in
                              `my-residents.spec.tsx` still resolves. */}
                          <button
                            type="button"
                            class="btn btn--quiet btn--destructive"
                            disabled={busy}
                            onClick={() => void removeResident(r)}
                          >
                            {myResidentsCopy.removeConfirmYes}
                          </button>
                          <button
                            type="button"
                            class="btn btn--secondary"
                            disabled={busy}
                            onClick={() => setArmedId(null)}
                          >
                            {myResidentsCopy.removeConfirmKeep}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div class="my-residents-actions">
                        <button
                          type="button"
                          id={editButtonId(r.id)}
                          class="btn btn--link"
                          onClick={() => startEdit(r)}
                        >
                          {myResidentsCopy.editButton}
                        </button>
                        <button
                          type="button"
                          class="btn btn--link"
                          onClick={() => {
                            setArmedId(r.id);
                            setRowNotice(null);
                          }}
                        >
                          {myResidentsCopy.removeButton}
                        </button>
                      </div>
                    ))}
                </>
              )}
              {rowNotice?.id === r.id && (
                <p class="field-error" role="alert">
                  {rowNotice.message}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ⛔ HIDDEN WHILE A ROW IS OPEN — see the header: two forms carrying the
          same label text at once is an ambiguous locator, and a busier screen. */}
      {writable && editingId === null && (
        <div class="my-residents-add">
          {adding ? (
        <form onSubmit={(e) => void addResident(e)}>
          <h2>{myResidentsCopy.addTitle}</h2>
          {fields('res', draft, addErrors, (patch) => setDraft((prev) => ({ ...prev, ...patch })), addNameRef)}
          {addNotice && (
            <p class="field-error" role="alert">
              {addNotice}
            </p>
          )}
          {putBack && (
            <>
              <p class="notice">{myResidentsCopy.removedOffer(putBack.name)}</p>
              <button
                type="button"
                class="btn btn--secondary"
                disabled={busy}
                onClick={() => void reactivate(putBack)}
              >
                {myResidentsCopy.putBackButton}
              </button>
            </>
          )}
          <div class="my-residents-actions">
            <button type="submit" class="btn btn--primary" disabled={busy}>
              {myResidentsCopy.addButton}
            </button>
            <button
              type="button"
              class="btn btn--secondary"
              disabled={busy}
              onClick={() => {
                setAdding(false);
                clearAddFeedback();
              }}
            >
              {myResidentsCopy.cancelEditButton}
            </button>
          </div>
        </form>
          ) : (
            <button
              type="button"
              class="btn btn--secondary"
              onClick={() => {
                setAdding(true);
                clearAddFeedback();
              }}
            >
              {myResidentsCopy.addTitle}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
